import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, XCircle } from 'lucide-react';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { getErrorMessage } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const schema = z.object({
  new_password: z.string().min(12, 'Password must be at least 12 characters'),
  confirm_password: z.string(),
}).refine((d) => d.new_password === d.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

const INVALID_MESSAGE = 'This reset link is invalid or has expired.';

const FieldError = ({ msg }) => msg ? (
  <p className="flex items-center gap-1 text-xs text-red-600 mt-1"><XCircle size={12} /> {msg}</p>
) : null;

const inputCls = (err) =>
  `w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-colors ${
    err ? 'border-red-400 focus:ring-red-300 bg-red-50' : 'border-border focus:ring-primary'
  }`;

const ResetPassword = () => {
  useDocumentHead({ robots: 'noindex' });
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [done, setDone] = useState(false);
  const [failure, setFailure] = useState(token ? '' : INVALID_MESSAGE);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { new_password: '', confirm_password: '' },
  });

  const onSubmit = async ({ new_password }) => {
    setFailure('');
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password });
      setDone(true);
    } catch (error) {
      setFailure(getErrorMessage(error, INVALID_MESSAGE));
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen pt-32 pb-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-md rounded-2xl overflow-hidden border border-border bg-white shadow-sm">
        <div className="bg-primary px-6 py-5">
          <h1 className="font-serif text-2xl font-bold text-white">Reset password</h1>
          <p className="text-white/80 text-sm">Choose a new password</p>
        </div>

        {done ? (
          <div className="px-6 pb-6 pt-5 space-y-4">
            <p data-testid="reset-password-success" className="text-sm text-foreground">Password updated.</p>
            <Link to="/login" className="text-primary font-semibold hover:underline text-sm">Go to login</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="px-6 pb-6 pt-4 space-y-5" noValidate>
            {failure && (
              <p data-testid="reset-password-error" className="flex items-center gap-1 text-sm text-red-600">
                <XCircle size={14} /> {failure}
              </p>
            )}

            <div>
              <label htmlFor="reset-new-password" className="block text-sm font-medium text-foreground mb-1.5">New password</label>
              <input {...register('new_password')} id="reset-new-password" data-testid="reset-password-input"
                type="password" autoComplete="new-password" placeholder="At least 12 characters" className={inputCls(errors.new_password)} />
              <FieldError msg={errors.new_password?.message} />
            </div>

            <div>
              <label htmlFor="reset-confirm-password" className="block text-sm font-medium text-foreground mb-1.5">Confirm password</label>
              <input {...register('confirm_password')} id="reset-confirm-password" data-testid="reset-password-confirm-input"
                type="password" autoComplete="new-password" placeholder="Repeat the new password" className={inputCls(errors.confirm_password)} />
              <FieldError msg={errors.confirm_password?.message} />
            </div>

            <button type="submit" disabled={isSubmitting || !token} data-testid="reset-password-submit-button"
              className="w-full bg-primary text-white py-2.5 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm flex items-center justify-center gap-2">
              {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Updating...</> : 'Update password'}
            </button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/forgot-password" className="text-primary font-semibold hover:underline">Request a new link</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
